import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, errorMessage } from "@atlas/shared";
import { createTrip, updateTrip } from "../api/trips";
import { ConfirmDialog } from "../components/ConfirmDialog";

// Grouping activities into a trip (spec 35), for every web entry point:
// "Make a trip" in Activities select mode, the suggestion card, and "Add
// activities" on the trip page. A 409 activity_in_other_trip opens a confirm
// and retries with moveFromOtherTrips once the user agrees.

type Request =
  | { kind: "create"; activityIds: string[]; name?: string }
  | { kind: "add"; tripId: string; activityIds: string[] };

function conflictIds(err: unknown): string[] | null {
  if (err instanceof ApiError && err.status === 409 && err.body?.["error"] === "activity_in_other_trip") {
    const ids = err.body["activityIds"];
    return Array.isArray(ids) ? ids.filter((x): x is string => typeof x === "string") : [];
  }
  return null;
}

export function tripErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.status === 409 && err.body?.["error"] === "trip_too_large") {
    return "A trip can hold up to 50 activities.";
  }
  return errorMessage(err, "Couldn't group those. Try again.");
}

export function useTripGrouping(opts: { onDone: (tripId: string) => void }) {
  const queryClient = useQueryClient();
  const [pendingMove, setPendingMove] = React.useState<{ request: Request; count: number } | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const onDoneRef = React.useRef(opts.onDone);
  onDoneRef.current = opts.onDone;

  const mutation = useMutation({
    mutationFn: async ({ request, move }: { request: Request; move: boolean }): Promise<string> => {
      if (request.kind === "create") {
        const res = await createTrip({
          activityIds: request.activityIds,
          ...(request.name ? { name: request.name } : {}),
          ...(move ? { moveFromOtherTrips: true } : {}),
        });
        return res.tripId;
      }
      await updateTrip(request.tripId, {
        addActivityIds: request.activityIds,
        ...(move ? { moveFromOtherTrips: true } : {}),
      });
      return request.tripId;
    },
    onSuccess: (tripId) => {
      setPendingMove(null);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["trips"] });
      void queryClient.invalidateQueries({ queryKey: ["activities"] });
      onDoneRef.current(tripId);
    },
    onError: (err, vars) => {
      const ids = conflictIds(err);
      if (ids && !vars.move) {
        setPendingMove({ request: vars.request, count: ids.length || vars.request.activityIds.length });
        return;
      }
      setPendingMove(null);
      setError(tripErrorMessage(err));
    },
  });

  const run = React.useCallback(
    (request: Request) => {
      setError(null);
      mutation.mutate({ request, move: false });
    },
    [mutation],
  );

  const confirmElement = pendingMove ? (
    <ConfirmDialog
      title="Move them to this trip?"
      body={
        pendingMove.count === 1
          ? "One of these is already in another trip. Move it here?"
          : `${pendingMove.count} of these are already in another trip. Move them here?`
      }
      confirmLabel="Move them here"
      pendingLabel="Moving…"
      pending={mutation.isPending}
      onConfirm={() => mutation.mutate({ request: pendingMove.request, move: true })}
      onClose={() => setPendingMove(null)}
    />
  ) : null;

  return {
    createTrip: (activityIds: string[], name?: string) => run({ kind: "create", activityIds, name }),
    addToTrip: (tripId: string, activityIds: string[]) => run({ kind: "add", tripId, activityIds }),
    pending: mutation.isPending,
    error,
    clearError: () => setError(null),
    confirmElement,
  };
}
